import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { IMAGES } from "@/constants/images";
import PageHero from "@/components/public/PageHero";
import EmptyState from "@/components/public/EmptyState";
import SectionHeading from "@/components/public/SectionHeading";
import CTASection from "@/components/public/CTASection";
import Reveal from "@/components/public/Reveal";
import { Ship } from "lucide-react";

export default function ExportJourney() {
  const [steps, setSteps] = useState([]);
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, r, st] = await Promise.all([api.get("/export-journey"), api.get("/export-records"), api.get("/statistics")]);
        setSteps(s.data); setRecords(r.data.filter(x => x.image)); setStats(st.data);
      } catch (e) {}
    })();
  }, []);

  return (
    <div>
      <PageHero eyebrow="Export Process" title="From Warehouse to the World" sub="Our export process, from coffee sourcing through to buyer delivery — presented transparently, step by step." image={IMAGES.ship} />

      {/* TIMELINE */}
      <section className="mx-auto max-w-[1400px] px-4 py-16 sm:px-8 lg:px-12 lg:py-24">
        <div className="relative">
          <div className="absolute left-[19px] top-2 bottom-2 w-px bg-forest/15 md:left-1/2" />
          <div className="space-y-10">
            {steps.map((s, i) => (
              <Reveal key={s.id || i}>
                <div className={`relative flex flex-col gap-4 md:flex-row md:items-center ${i % 2 ? "md:flex-row-reverse" : ""}`}>
                  <div className="flex items-center gap-4 md:w-1/2 md:justify-end md:pr-10" style={i % 2 ? { paddingRight: 0, paddingLeft: "2.5rem", justifyContent: "flex-start" } : {}}>
                    <div className={`${i % 2 ? "md:order-2" : ""} rounded-xl border border-forest/10 bg-white p-6 shadow-sm md:max-w-sm`}>
                      <div className="font-mono text-xs font-semibold uppercase tracking-wider text-amber-brand">Step {s.step}</div>
                      <h3 className="mt-2 font-display text-lg font-bold text-forest-dark">{s.title}</h3>
                      <p className="mt-2 font-body text-sm leading-relaxed text-espresso/70">{s.description}</p>
                      {s.image && <img src={s.image} alt={s.title} loading="lazy" className="mt-4 aspect-video w-full rounded-lg object-cover" />}
                    </div>
                  </div>
                  <div className="absolute left-0 top-6 flex h-10 w-10 items-center justify-center rounded-full border-2 border-amber-brand bg-forest font-display text-sm font-bold text-offwhite md:left-1/2 md:-translate-x-1/2 md:top-1/2 md:-translate-y-1/2">{s.step}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      {stats.length > 0 && (
        <section className="bg-forest-dark py-16">
          <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-12">
            <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
              {stats.map((st) => (
                <div key={st.id} className="text-center">
                  <div className="font-display text-3xl font-extrabold text-amber-soft lg:text-4xl">{st.value}</div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-offwhite/55">{st.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TRACK RECORD */}
      <section className="bg-beige py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-12">
          <SectionHeading eyebrow="Export in Action" title="Our Export Journey" sub="Real evidence of trading and export activity — warehouse preparation, loading, port activities and shipments. Sensitive details on documents are redacted before public display." />
          <div className="mt-12">
            {records.length === 0 ? (
              <EmptyState message="Export track record photos will be published here as they are added by our team." icon={Ship} testId="export-records-empty" />
            ) : (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {records.map((r) => (
                  <figure key={r.id} className="group overflow-hidden rounded-xl border border-forest/10 bg-white">
                    <div className="aspect-[4/3] overflow-hidden"><img src={r.image} alt={r.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
                    <figcaption className="p-5">
                      <div className="font-display text-base font-bold text-forest-dark">{r.title}</div>
                      {r.description && <p className="mt-1 font-body text-sm text-espresso/65">{r.description}</p>}
                      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[10px] uppercase tracking-wider text-espresso/50">
                        {r.date && <span>{r.date}</span>}
                        {r.destination && <span>→ {r.destination}</span>}
                        {r.shipment_ref && <span>Ref {r.shipment_ref}</span>}
                        {r.quantity && <span>{r.quantity}</span>}
                      </div>
                    </figcaption>
                  </figure>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
      <CTASection />
    </div>
  );
}
